import * as Sentry from "@sentry/tanstackstart-react";
import { Link } from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { Icon } from "icons";
import { useEffect } from "react";
import { Button } from "ui";

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.stack ?? error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object") {
    const maybeMessage = (error as { message?: unknown }).message;

    if (typeof maybeMessage === "string") {
      return maybeMessage;
    }

    try {
      return JSON.stringify(error, null, 2);
    } catch {
      return String(error);
    }
  }

  return String(error);
}

export function DefaultCatchBoundary({ error }: ErrorComponentProps) {
  console.error("DefaultCatchBoundary Error:", error);

  useEffect(() => {
    Sentry.captureException(error, {
      mechanism: { type: "tanstack_router", handled: true },
    });
  }, [error]);

  const errorMessage = getErrorMessage(error);

  return (
    <div className="min-w-0 flex-1 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md flex flex-col items-center text-center">
        <div className="flex size-16 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <Icon name="tabler-alert-circle" size={32} strokeWidth={1.5} />
        </div>

        <h1 className="mt-6 text-xl font-semibold tracking-tight text-foreground">
          Something went wrong
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          An unexpected error occurred. Try refreshing the page, or head back
          home to continue.
        </p>

        {errorMessage ? (
          <details className="group mt-6 w-full text-left">
            <summary className="flex cursor-pointer list-none items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/70">
              <span>Error details</span>
              <Icon
                name="tabler-chevron-down"
                size={16}
                className="transition-transform group-open:rotate-180"
              />
            </summary>
            <pre className="mt-2 max-h-48 overflow-auto rounded-md border bg-muted/40 p-3 text-left text-xs leading-relaxed text-muted-foreground">
              {errorMessage}
            </pre>
          </details>
        ) : null}

        <div className="mt-8 flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
          <Button
            onClick={() => {
              window.location.reload();
            }}
          >
            <Icon name="tabler-refresh" size={16} />
            Refresh page
          </Button>
          <Button variant="outline" asChild>
            <Link to="/">Go home</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
